import React from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowRight } from 'blaze-ui/icons'
import { ExampleBrands } from './ExampleBrands/ExampleBrands'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { useBrandKitContext } from './BrandKitContext'
import Styles from './BrandKitForm.module.scss'
import { ExampleBrand } from 'types'
import { Button } from 'blaze-ui/Button'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import cx from 'classnames'
interface BrandKitFormProps {
  /** Optional platform from the URL param (e.g., "Instagram") */
  platform?: string | null
  onBack?: () => void
  onSkip?: () => void
  title?: string
  withinOnboarding?: boolean
  useIntegrations?: boolean
}

const exampleBrandUrls: Record<string, string> = {
  Nike: 'https://nike.com/',
  Rains: 'https://rains.com/',
  Pacha: 'https://pachasoap.com/',
}

/**
 * Renders the normal brand kit creation UI:
 *  - Sample brands
 *  - "ConnectIntegration" block
 *  - Website input
 */
export const BrandKitForm: React.FC<BrandKitFormProps> = ({ platform, onBack, onSkip, title, withinOnboarding }) => {
  const [selectedExampleBrand, setSelectedExampleBrand] = React.useState<ExampleBrand | null>(null)
  const {
    url,
    setUrl,
    isDirty,
    setIsDirty,
    isValid,
    errorMessage,
    isBlank,
    inputProps,
    onGenerateFromWebsite,
    onGenerateFromExampleBrand,
  } = useBrandKitContext()

  const onExampleBrandSelect = (brand: ExampleBrand) => {
    setSelectedExampleBrand(brand)
    setUrl(exampleBrandUrls[brand.name])
  }

  const handleGenerateFromWebsite = () => {
    if (selectedExampleBrand && url === exampleBrandUrls[selectedExampleBrand.name]) {
      onGenerateFromExampleBrand(selectedExampleBrand)
    } else {
      onGenerateFromWebsite()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      setIsDirty(false)
      return
    }

    handleGenerateFromWebsite()
  }

  return (
    <MultiStepStepLayout title={title} onBack={onBack} onSkip={onSkip}>
      <ResponsiveDesign.Desktop>
        <div className={Styles.container}>
          <div className={cx(Styles.websiteInputContainer, { [Styles.withinOnboarding]: withinOnboarding })}>
            <div className={Styles.websiteSection}>
              <Input
                {...inputProps}
                value={url}
                errorMessage={isDirty && !isValid ? errorMessage : undefined}
                placeholder="Enter your website"
                size="xl"
                className={Styles.websiteField}
                onKeyDown={handleKeyDown}
                autoFocus
                autoCorrect="off"
              />
              <Button
                variant="primary"
                size="xl"
                onPress={handleGenerateFromWebsite}
                isDisabled={isBlank}
                endIcon={ArrowRight}
                aria-label="Submit"
              >
                Submit
              </Button>
            </div>
            <Text variant="metadata" color="var(--dark-60)">
              No website? Use an industry leader's site you like and adjust the Brand Kit to your liking.
            </Text>
          </div>
          <div className={Styles.exampleBrandsContainer}>
            <Text color="var(--dark-80)">
              Just trying us out? <br /> Create a Brand Kit with an example brand:
            </Text>
            <ExampleBrands onExampleBrandSelect={onExampleBrandSelect} />
          </div>
        </div>
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <div className={Styles.container}>
          <div className={Styles.section}>
            <div className={Styles.mobileWebsiteSection}>
              <Heading level={4}>Create a Brand Kit in one click:</Heading>
              <div className={Styles.websiteInput}>
                <Input
                  {...inputProps}
                  value={url}
                  errorMessage={isDirty && !isValid ? errorMessage : undefined}
                  placeholder="Enter your website"
                  size="xl"
                  className={Styles.websiteField}
                  variant="grey"
                  autoFocus
                  onKeyDown={handleKeyDown}
                  autoCorrect="off"
                />
                <div className={Styles.button}>
                  <IconButton
                    variant="primary"
                    size="xl"
                    onPress={handleGenerateFromWebsite}
                    isDisabled={isBlank}
                    icon={ArrowRight}
                    aria-label="Submit"
                  />
                </div>
              </div>
            </div>

            <Text variant="placeholder" color="var(--dark-60)">
              No website? Use an industry leader's site you like and adjust the Brand Kit to your liking.
            </Text>
          </div>
          <div className={cx(Styles.section, Styles.exampleBrands)}>
            <Text variant="placeholder" color="var(--dark-80)">
              Just checking things out? Create a Brand Kit with an example brand:
            </Text>
            <ExampleBrands onExampleBrandSelect={onExampleBrandSelect} />
          </div>
        </div>
      </ResponsiveDesign.Mobile>
    </MultiStepStepLayout>
  )
}
